// src/pages/MyArea.tsx
import React, { useState, useEffect } from "react";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import "../styles/MyArea.css";

interface SavedArea {
  id: number;
  force: string;              // short key, e.g. "metropolitan"
  forceName?: string;         // full force name from the join in getSavedAreas
  neighbourhood: string;      // neighbourhood name
  neighbourhoodID: string;    // neighbourhood short code (hidden field)
  friendlyName: string;
  communityInformationSMS: boolean;
  communityInformationEmail: boolean;
  crimePreventionSMS: boolean;
  crimePreventionEmail: boolean;
  policeSurgeriesSMS: boolean;
  policeSurgeriesEmail: boolean;
}

const MyArea: React.FC = () => {
  const { t } = useTranslation();
  const translate = t as (key: string, options?: any) => string;
  
  const [address, setAddress] = useState("");
  const [selectedAddress, setSelectedAddress] = useState("");
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [policeData, setPoliceData] = useState<{
    force: { code: string; name: string };
    neighbourhood: { code: string; name: string; description?: string };
  } | null>(null);
  const [error, setError] = useState("");
  const [savedAreas, setSavedAreas] = useState<SavedArea[]>([]);
  const [isPoliceLoading, setIsPoliceLoading] = useState(false);

  // Retrieve oneLoginSub from localStorage
  const oneLoginSub = localStorage.getItem("oneLoginSub") || "";

  // Fetch saved areas on mount using oneLoginSub
  useEffect(() => {
    if (oneLoginSub) {
      const fetchSavedAreas = async () => {
        try {
          const response = await fetch(`https://api.psanalytics.co.uk/ncjp/getSavedAreas?oneLoginSub=${encodeURIComponent(oneLoginSub)}`);
          const data = await response.json();
          if (data.status === "success" && Array.isArray(data.areas)) {
            setSavedAreas(data.areas);
          } else {
            console.error("Error fetching saved areas:", data.message);
          }
        } catch (err) {
          console.error("Error fetching saved areas:", err);
        }
      };
      fetchSavedAreas();
    }
  }, [oneLoginSub]);

  const handleSelect = async (value: string) => {
    setAddress(value);
    setSelectedAddress(value);
    setError("");
    setCoordinates(null);
    setPoliceData(null);
    setIsPoliceLoading(true);
    
    try {
      const results = await geocodeByAddress(value);
      const latLng = await getLatLng(results[0]);
      setCoordinates(latLng);
    } catch (err) {
      console.error("Error fetching geocode data:", err);
      setError(translate("errorFetchingGeocode") || "Error fetching geocode data.");
      setIsPoliceLoading(false);
    }
  };

  // When coordinates are set, call the police.uk proxy
  useEffect(() => {
    if (coordinates) {
      const fetchPoliceData = async () => {
        try {
          const response = await fetch(
            `https://api.psanalytics.co.uk/ncjp/policeuk?lat=${coordinates.lat}&lng=${coordinates.lng}`
          );
          const data = await response.json();
          if (data.status === "error") {
            setError(data.message || translate("errorFetchingPoliceData") || "Error fetching police data");
          } else {
            setPoliceData(data);
          }
        } catch (err) {
          console.error("Error fetching policeUK data:", err);
          setError(translate("errorFetchingPoliceData") || "Error fetching police data.");
        } finally {
          setIsPoliceLoading(false);
        }
      };
      fetchPoliceData();
    }
  }, [coordinates, translate]);

  // Add new area based on policeData
  const addArea = () => {
    if (!policeData) return;
    // Prevent duplicates based on force and neighbourhood
    if (savedAreas.some(area => area.force === policeData.force.code && area.neighbourhood === policeData.neighbourhood.name)) {
      return;
    }
    if (savedAreas.length >= 4) return;
    
    const newArea: SavedArea = {
      id: Date.now(),
      force: policeData.force.code,                // short key
      neighbourhood: policeData.neighbourhood.name,  // name
      neighbourhoodID: policeData.neighbourhood.code, // hidden field
      friendlyName: "",
      communityInformationSMS: false,
      communityInformationEmail: false,
      crimePreventionSMS: false,
      crimePreventionEmail: false,
      policeSurgeriesSMS: false,
      policeSurgeriesEmail: false,
    };
    const updatedAreas = [...savedAreas, newArea];
    setSavedAreas(updatedAreas);
    saveAreas(updatedAreas);
  };

  // Update a saved area
  const updateArea = (id: number, field: keyof SavedArea, value: any) => {
    const updatedAreas = savedAreas.map(area =>
      area.id === id ? { ...area, [field]: value } : area
    );
    setSavedAreas(updatedAreas);
    saveAreas(updatedAreas);
  };

  // Delete a saved area
  const deleteArea = async (areaID: number) => {
    try {
      const response = await fetch(
        `https://api.psanalytics.co.uk/ncjp/deleteArea?oneLoginSub=${encodeURIComponent(oneLoginSub)}&areaID=${areaID}`,
        { method: "DELETE" }
      );
      const data = await response.json();
      if (data.status === "success") {
        const remainingAreas = savedAreas.filter(area => area.id !== areaID);
        setSavedAreas(remainingAreas);
      } else {
        console.error("Error deleting area:", data.message);
      }
    } catch (err) {
      console.error("Error deleting area:", err);
    }
  };

  // Save areas API call with oneLoginSub included
  const saveAreas = async (areas: SavedArea[]) => {
    if (!oneLoginSub) {
      console.error("oneLoginSub is missing from localStorage");
      return;
    }
    try {
      await fetch(`https://api.psanalytics.co.uk/ncjp/saveAreas?oneLoginSub=${encodeURIComponent(oneLoginSub)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ areas }),
      });
    } catch (err) {
      console.error("Error saving areas:", err);
    }
  };

  return (
    <div>
      <Navbar />
      {isPoliceLoading && (
        <div className="spinner-overlay">
          <div className="spinner"></div>
        </div>
      )}
      <div className="govuk-main-wrapper" style={{ display: "flex", justifyContent: "center", marginTop: "80px" }}>
        <div style={{ maxWidth: "800px", width: "100%", padding: "0 1rem" }}>
          <h1 className="govuk-heading-xl" style={{ textAlign: "center" }}>
            {translate("myAreaTitle") || "Find My Area"}
          </h1>
          <p className="govuk-body" style={{ textAlign: "center" }}>
            {translate("myAreaDescription") || "Enter your address to find your local neighbourhood information."}
          </p>
          <PlacesAutocomplete value={address} onChange={setAddress} onSelect={handleSelect}>
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
              <div style={{ textAlign: "center" }}>
                <input
                  {...getInputProps({
                    placeholder: translate("enterAddressPlaceholder") || "Enter address or postcode",
                    className: "govuk-input",
                  })}
                  style={{ margin: "0 auto", display: "block", maxWidth: "100%", textAlign: "center" }}
                />
                <div
                  className="autocomplete-dropdown-container"
                  style={{
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    marginTop: "0.5rem",
                    backgroundColor: "#fff",
                    fontSize: "1.1rem",
                    textAlign: "left",
                    padding: "0.5rem",
                  }}
                >
                  {loading && <div>{translate("loading") || "Loading..."}</div>}
                  {suggestions.map((suggestion) => {
                    const className = suggestion.active ? "suggestion-item--active" : "suggestion-item";
                    const style = suggestion.active
                      ? { backgroundColor: "#fafafa", cursor: "pointer", padding: "0.75rem" }
                      : { backgroundColor: "#ffffff", cursor: "pointer", padding: "0.75rem" };
                    return (
                      <div {...getSuggestionItemProps(suggestion, { className, style })}>
                        <span>{suggestion.description}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </PlacesAutocomplete>
          {error && (
            <p className="govuk-error-message" style={{ textAlign: "center" }}>
              {error}
            </p>
          )}
          {policeData && !isPoliceLoading && (
            <div style={{ marginTop: "20px", textAlign: "center" }}>
              <h2 className="govuk-heading-m">
                {translate("selectedAreaTitle") || "Selected Police Force and Neighbourhoods"}
              </h2>
              <p className="govuk-body">
                {translate("policeForceLabel") || "Police Force"}: {policeData.force.name}
              </p>
              <p className="govuk-body">
                {translate("localNeighbourhoodLabel") || "Local Neighbourhood"}: {policeData.neighbourhood.name}
              </p>
              <div style={{ marginTop: "1rem" }}>
                <p className="govuk-body" style={{ marginBottom: "0.5rem" }}>
                  {translate("addAreaInstructions")}
                </p>
                <button className="govuk-button" onClick={addArea}>
                  {translate("addAreaButton")}
                </button>
              </div>
            </div>
          )}
          {savedAreas.length > 0 && (
            <div style={{ marginTop: "40px" }}>
              <h2 className="govuk-heading-m" style={{ textAlign: "center" }}>
                {translate("selectedAreaTitle")}
              </h2>
              <div className="table-container">
                <table className="govuk-table">
                  <thead>
                    <tr>
                      <th>{translate("policeForceLabel")}</th>
                      <th>{translate("localNeighbourhoodLabel")}</th>
                      <th>{translate("friendlyNameLabel")}</th>
                      <th>{translate("communityInformationLabel")}</th>
                      <th>{translate("crimePreventionLabel")}</th>
                      <th>{translate("policeSurgeriesLabel")}</th>
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedAreas.map((area) => (
                      <tr key={area.id}>
                        <td style={{ verticalAlign: "top", padding: "0.5rem" }}>
                          {area.forceName ? area.forceName : area.force}
                        </td>
                        <td style={{ verticalAlign: "top", padding: "0.5rem" }}>{area.neighbourhood}</td>
                        <td style={{ verticalAlign: "top", padding: "0.5rem" }}>
                          <input
                            type="text"
                            value={area.friendlyName}
                            onChange={(e) => updateArea(area.id, "friendlyName", e.target.value)}
                            className="govuk-input friendly-name-input"
                            placeholder={translate("friendlyNameLabel")}
                          />
                          <input type="hidden" value={area.neighbourhoodID} />
                        </td>
                        <td style={{ verticalAlign: "top", padding: "0.5rem" }}>
                          <div className="notification-group">
                            <label>
                              <input
                                type="checkbox"
                                checked={area.communityInformationSMS}
                                onChange={(e) => updateArea(area.id, "communityInformationSMS", e.target.checked)}
                              />{" "}
                              {translate("notificationSMS")}
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={area.communityInformationEmail}
                                onChange={(e) => updateArea(area.id, "communityInformationEmail", e.target.checked)}
                              />{" "}
                              {translate("notificationEmail")}
                            </label>
                          </div>
                        </td>
                        <td style={{ verticalAlign: "top", padding: "0.5rem" }}>
                          <div className="notification-group">
                            <label>
                              <input
                                type="checkbox"
                                checked={area.crimePreventionSMS}
                                onChange={(e) => updateArea(area.id, "crimePreventionSMS", e.target.checked)}
                              />{" "}
                              {translate("notificationSMS")}
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={area.crimePreventionEmail}
                                onChange={(e) => updateArea(area.id, "crimePreventionEmail", e.target.checked)}
                              />{" "}
                              {translate("notificationEmail")}
                            </label>
                          </div>
                        </td>
                        <td style={{ verticalAlign: "top", padding: "0.5rem" }}>
                          <div className="notification-group">
                            <label>
                              <input
                                type="checkbox"
                                checked={area.policeSurgeriesSMS}
                                onChange={(e) => updateArea(area.id, "policeSurgeriesSMS", e.target.checked)}
                              />{" "}
                              {translate("notificationSMS")}
                            </label>
                            <label>
                              <input
                                type="checkbox"
                                checked={area.policeSurgeriesEmail}
                                onChange={(e) => updateArea(area.id, "policeSurgeriesEmail", e.target.checked)}
                              />{" "}
                              {translate("notificationEmail")}
                            </label>
                          </div>
                        </td>
                        <td style={{ verticalAlign: "top", padding: "0.5rem", textAlign: "center" }}>
                          <button
                            className="govuk-button delete-button"
                            onClick={() => deleteArea(area.id)}
                            title="Delete Area"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
            <Link to="/" className="govuk-button">
              {translate("backToDashboard") || "Back to Dashboard"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyArea;
  